import { cn } from "@/lib/utils";

interface StageFunnelStripProps {
  statuses: { title: string; color?: string }[];
  dealCountByStatus: Record<string, number>;
  // Currently-active statuses. Empty array means "All" is active.
  activeStatuses: string[];
  // Click handler. Receives the clicked status title; parent toggles it
  // in/out of the activeStatuses array. Receives null when "All" is clicked.
  onStatusClick: (status: string | null) => void;
}

interface ChipProps {
  count: number;
  label: string;
  isActive: boolean;
  onClick: () => void;
  ariaLabel: string;
}

// Compact stage chip — single line, caps label + mono count. The strip is a
// filter row, not an analytics graphic; the count carries the funnel shape
// well enough on its own.
function StageChip({ count, label, isActive, onClick, ariaLabel }: ChipProps) {
  return (
    <button
      type="button"
      aria-pressed={isActive}
      aria-label={ariaLabel}
      onClick={onClick}
      className={cn(
        "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border px-2.5 transition-colors",
        isActive
          ? "border-foreground/20 bg-foreground/[0.06] text-foreground"
          : "border-border bg-background text-muted-foreground hover:text-foreground hover:bg-foreground/[0.03]"
      )}
    >
      <span className="text-[10px] font-semibold uppercase tracking-widest whitespace-nowrap">
        {label}
      </span>
      <span
        className={cn(
          "font-mono text-xs tabular-nums",
          count === 0
            ? "text-muted-foreground/40"
            : isActive
            ? "text-foreground"
            : "text-foreground/80"
        )}
      >
        {count}
      </span>
    </button>
  );
}

export function StageFunnelStrip({
  statuses,
  dealCountByStatus,
  activeStatuses,
  onStatusClick,
}: StageFunnelStripProps) {
  const isAllActive = activeStatuses.length === 0;
  const totalCount = Object.values(dealCountByStatus).reduce(
    (sum, n) => sum + n,
    0
  );

  return (
    <div
      role="group"
      aria-label="Filter by stage"
      className="hidden md:flex shrink-0 items-center gap-1.5 bg-background md:px-6 md:py-2.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <StageChip
        count={totalCount}
        label="All"
        isActive={isAllActive}
        onClick={() => onStatusClick(null)}
        ariaLabel={`Show all deals, ${totalCount} total`}
      />
      {statuses.map((status) => {
        const count = dealCountByStatus[status.title] ?? 0;
        const isActive = activeStatuses.includes(status.title);
        return (
          <StageChip
            key={status.title}
            count={count}
            label={status.title}
            isActive={isActive}
            onClick={() => onStatusClick(status.title)}
            ariaLabel={`${status.title}, ${count} deals`}
          />
        );
      })}
    </div>
  );
}
